import React, {useCallback, useState} from 'react';
import styled from 'styled-components';
import SVG from "react-inlinesvg";
import BigNumber from "bignumber.js";
import Loader from "react-loader-spinner";
import {useDepositShef} from "../hooks/useStake";
import {BIG_ZERO, getBalanceNumber} from "../utils/formatBalance";
import {ButtonAppearanceEnum} from "./Button/types";
import Button from "./Button";
import {useFarmFromPid} from "../state/hooks";
import {useERC20} from "../hooks/useContract";
import {getBusdAddress} from "../utils/addressHelpers";
import goodImg from "../assets/txstatus/check.png";
import failImg from "../assets/txstatus/cancel.png";
import useApprove from "../hooks/useApprove";
import cancelIcon from "../assets/cancel.svg";


const BlockWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: linear-gradient(130.22deg, rgba(145, 153, 255, 0.75) 26.49%, rgba(60, 149, 219, 0.75) 100.77%);
  width: 100vw;
  height: 100vh;
  z-index: 100;
`;


const Block = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  padding: 30px;
  border: 0.3px solid #a2a2a2;
  border-radius: 10px;
  background: #fff;
  width: 100%;
  max-width: 400px;
  @media (max-width: 768px) {
    width: calc(100% - 20px);
  }
`;

const Title = styled.h2`
  font-size: ${({theme}) => theme.fontSizes[3]};
  font-weight: 600;
  margin-bottom: 12px;
`;

const StyledTokenInput = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #F2F4F5;
  margin-top: 10px;
  border-radius: 5px;
`;

const StyledInput = styled.input`
  box-shadow: none;
  width: 100%;
  margin: 0 8px;
  padding: 0 8px;
  background: none;
  border: 0;
  outline: none;
  color: #000;
  background: #F2F4F5;
`;

const Text = styled.p`
  color: ${({theme}) => theme.colors.secondary};
  font-weight: 600;
  margin-bottom: ${({theme}) => theme.space[3]};
  font-size: ${({theme}) => theme.fontSizes[1]};
`;


const Cancel = styled.div`
  position: relative;
  left: 45%;

  svg {
    width: 30px;
    height: 30px;
    cursor: pointer;
    padding: 8px;
    margin: 0;

    :hover {
      opacity: ${({theme}) => theme.opacity};
    }
  }

  path {
    fill: #000;
  }
`;

const StatusImg = styled.img`
  width: 76px;
  height: 76px;
  margin: 10px;
  margin-top: 40px;
`;

const StyledButton = styled(Button)`
  margin-top: 20px;
  width: 100%;
  padding: 13px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  margin-top: 4px;
`;


interface DepositModalRobotsProps {
    setShowDeposit: any;
    handleChange: any;
    handleSelectMax: any;
    val: string;
    max: BigNumber;
}

const DepositModalContract: React.FC<DepositModalRobotsProps> = ({
                                                                     setShowDeposit,
                                                                     handleChange,
                                                                     handleSelectMax,
                                                                     val,
                                                                     max
                                                                 }) => {

    const {onStake} = useDepositShef();
    const [pendingTx, setPendingTx] = useState(false);

    const state = useFarmFromPid(0);
    const allowance = state && state.userData && state.userData.busdAllowance && new BigNumber(state.userData.busdAllowance) || BIG_ZERO;
    const isApproved = allowance.toNumber() > 0;

    const [requestedApproval, setRequestedApproval] = useState(false);
    const busdContract = useERC20(getBusdAddress());
    const {onApprove} = useApprove(busdContract);
    const handleApproveToken = useCallback(async () => {
        try {
            setRequestedApproval(true);
            const txHash = await onApprove();
            // user rejected tx or didn't go thru
            if (!txHash) {
                setRequestedApproval(false);
            }
        } catch (e) {
            console.error(e);
        }
    }, [onApprove]);

    const [txStatus, setTxStatus] = useState("NONE");
    const handleStake = useCallback(async () => {
        try {
            setPendingTx(true);
            const txHash = await onStake(val);
            if (txHash) {
                setTxStatus("GOOD");
            }
            if (!txHash) {
                setPendingTx(false);
                setTxStatus("NONE");
            }
        } catch (e) {
            console.error(e);
            setTxStatus("FAIL");
        }
    }, [onStake, val]);
    const isDepositDisabled = pendingTx || val === '0' || val === '' || parseFloat(val) > getBalanceNumber(max)

    return (
        <BlockWrapper>
            <Block>
                <Cancel onClick={() => setShowDeposit(false)}>
                    <SVG src={cancelIcon}/>
                </Cancel>

                {txStatus === "NONE" && <Title>DEPOSIT</Title>}
                {txStatus === "GOOD" && <Title>DEPOSIT COMPLETED </Title>}
                {txStatus === "GOOD" && <p>You can close the tab</p>}
                {txStatus === "FAIL" && <Title>DEPOSIT IS FAILED</Title>}
                {txStatus === "FAIL" && <p>You can close the tab and try again</p>}
                {txStatus === "GOOD" && <StatusImg src={goodImg} alt="good"/>}
                {txStatus === "FAIL" && <StatusImg src={failImg} alt="fail"/>}

                {txStatus === "NONE" &&
                <>
                    <Text>BUSD available: {getBalanceNumber(max).toFixed(6)}</Text>
                    <StyledTokenInput>
                        <StyledInput onChange={handleChange} placeholder="0" type="number" step="1" value={val}/>
                        <Button onClick={handleSelectMax}
                                appearance={ButtonAppearanceEnum.primary}
                                style={{padding: '10px 20px'}}>
                            Max
                        </Button>
                    </StyledTokenInput>
                    {!isApproved && <StyledButton appearance={ButtonAppearanceEnum.primary}
                                                  onClick={handleApproveToken}
                                                  disabled={requestedApproval}>
                        {requestedApproval ? "Pending" : "Approve BUSD"}
                    </StyledButton>}
                    {isApproved && <StyledButton appearance={ButtonAppearanceEnum.primary}
                                                 disabled={isDepositDisabled}
                                                 style={{background: isDepositDisabled && "rgb(152,148,148)"}}
                                                 onClick={handleStake}>
                        <p>{pendingTx ? "Pending" : "Deposit"}</p>
                        {pendingTx &&
                        <LoaderWrapper>
                            <Loader type="ThreeDots" color="#fff" height={25} width={25}/>
                        </LoaderWrapper>}
                    </StyledButton>}
                </>
                }
            </Block>
        </BlockWrapper>
    );
};

export default DepositModalContract;
